export const DIAS_SEMANA = [
  { id: "lunes", label: "Lunes" },
  { id: "martes", label: "Martes" },
  { id: "miercoles", label: "Miércoles" },
  { id: "jueves", label: "Jueves" },
  { id: "viernes", label: "Viernes" },
  { id: "sabado", label: "Sábado" },
  { id: "domingo", label: "Domingo" },
];

export const TURNOS_HORARIO = [
  { id: "matutino", label: "Matutino / Desayunos" },
  { id: "vespertino", label: "Vespertino" },
];

export const esHoraValida = (valor) => {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(String(valor || ""));
};

export const horaAMinutos = (hora) => {
  if (!esHoraValida(hora)) return null;

  const [horas, minutos] = hora.split(":").map(Number);

  return horas * 60 + minutos;
};

const crearDiaVacio = () => ({
  activo: false,
  apertura: "",
  cierrePedidos: "",
});

const crearTurnoVacio = () => {
  const turno = {};

  DIAS_SEMANA.forEach((dia) => {
    turno[dia.id] = crearDiaVacio();
  });

  return turno;
};

export const crearHorariosVacios = () => ({
  mensajeCerrado: "",
  suspension: {
    activa: false,
    tipo: "ninguna",
    hasta: null,
    mensaje: "",
  },
  turnos: {
    matutino: crearTurnoVacio(),
    vespertino: crearTurnoVacio(),
  },
});

export const normalizarHorariosAtencion = (horariosGuardados) => {
  const base = crearHorariosVacios();

  if (!horariosGuardados || typeof horariosGuardados !== "object") {
    return base;
  }

  const normalizados = {
    ...base,
    mensajeCerrado: horariosGuardados.mensajeCerrado || "",
    suspension: {
      ...base.suspension,
      ...(horariosGuardados.suspension || {}),
    },
    turnos: {
      ...base.turnos,
    },
  };

  TURNOS_HORARIO.forEach((turno) => {
    normalizados.turnos[turno.id] = {
      ...base.turnos[turno.id],
    };

    DIAS_SEMANA.forEach((dia) => {
      const diaGuardado = horariosGuardados.turnos?.[turno.id]?.[dia.id];

      normalizados.turnos[turno.id][dia.id] = {
        ...base.turnos[turno.id][dia.id],
        ...(diaGuardado || {}),
        activo: Boolean(diaGuardado?.activo),
        apertura: diaGuardado?.apertura || "",
        cierrePedidos: diaGuardado?.cierrePedidos || "",
      };
    });
  });

  return normalizados;
};

export const validarHorariosAtencion = (horariosAtencion) => {
  const errores = [];

  TURNOS_HORARIO.forEach((turno) => {
    DIAS_SEMANA.forEach((dia) => {
      const configDia = horariosAtencion?.turnos?.[turno.id]?.[dia.id];

      if (!configDia?.activo) return;

      if (!esHoraValida(configDia.apertura)) {
        errores.push(
          `${turno.label} / ${dia.label}: falta hora de apertura válida.`,
        );
      }

      if (!esHoraValida(configDia.cierrePedidos)) {
        errores.push(
          `${turno.label} / ${dia.label}: falta hora de cierre de pedidos válida.`,
        );
      }

      const aperturaMinutos = horaAMinutos(configDia.apertura);
      const cierreMinutos = horaAMinutos(configDia.cierrePedidos);

      if (
        aperturaMinutos !== null &&
        cierreMinutos !== null &&
        cierreMinutos <= aperturaMinutos
      ) {
        errores.push(
          `${turno.label} / ${dia.label}: el cierre de pedidos debe ser posterior a la apertura.`,
        );
      }
    });
  });

  return {
    valido: errores.length === 0,
    errores,
  };
};

export const obtenerDiaActualId = (fecha = new Date()) => {
  const diaSemana = fecha.getDay();

  const mapaDias = {
    0: "domingo",
    1: "lunes",
    2: "martes",
    3: "miercoles",
    4: "jueves",
    5: "viernes",
    6: "sabado",
  };

  return mapaDias[diaSemana];
};

export const obtenerLabelDia = (diaId) => {
  return DIAS_SEMANA.find((dia) => dia.id === diaId)?.label || diaId;
};

export const formatearHoraCliente = (hora) => {
  if (!esHoraValida(hora)) return "";

  const [horas, minutos] = hora.split(":").map(Number);
  const fecha = new Date();

  fecha.setHours(horas, minutos, 0, 0);

  return fecha.toLocaleTimeString("es-MX", {
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatearFechaHoraCliente = (iso) => {
  if (!iso) return "";

  try {
    return new Date(iso).toLocaleString("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
};

const obtenerTurnosAbiertosAhora = (horariosAtencion, fechaActual) => {
  const diaActualId = obtenerDiaActualId(fechaActual);
  const minutosActuales =
    fechaActual.getHours() * 60 + fechaActual.getMinutes();

  const turnosAbiertos = [];

  TURNOS_HORARIO.forEach((turno) => {
    const configDia = horariosAtencion?.turnos?.[turno.id]?.[diaActualId];

    if (!configDia?.activo) return;

    const aperturaMinutos = horaAMinutos(configDia.apertura);
    const cierreMinutos = horaAMinutos(configDia.cierrePedidos);

    if (aperturaMinutos === null || cierreMinutos === null) return;

    if (minutosActuales >= aperturaMinutos && minutosActuales < cierreMinutos) {
      turnosAbiertos.push({
        turno: turno.id,
        label: turno.label,
        apertura: configDia.apertura,
        cierrePedidos: configDia.cierrePedidos,
      });
    }
  });

  return turnosAbiertos;
};

const obtenerProximaApertura = (horariosAtencion, fechaActual) => {
  const minutosActuales =
    fechaActual.getHours() * 60 + fechaActual.getMinutes();

  for (let offset = 0; offset < 7; offset += 1) {
    const fechaEvaluada = new Date(fechaActual);
    fechaEvaluada.setDate(fechaActual.getDate() + offset);

    const diaId = obtenerDiaActualId(fechaEvaluada);

    const aperturasDelDia = [];

    TURNOS_HORARIO.forEach((turno) => {
      const configDia = horariosAtencion?.turnos?.[turno.id]?.[diaId];

      if (!configDia?.activo) return;

      const aperturaMinutos = horaAMinutos(configDia.apertura);

      if (aperturaMinutos === null) return;

      if (offset === 0 && aperturaMinutos <= minutosActuales) return;

      aperturasDelDia.push({
        diaId,
        diaLabel: obtenerLabelDia(diaId),
        turno: turno.id,
        turnoLabel: turno.label,
        apertura: configDia.apertura,
        aperturaMinutos,
      });
    });

    if (aperturasDelDia.length > 0) {
      aperturasDelDia.sort((a, b) => a.aperturaMinutos - b.aperturaMinutos);

      return aperturasDelDia[0];
    }
  }

  return null;
};

export const obtenerEstadoTienda = (
  horariosGuardados,
  fechaActual = new Date(),
) => {
  const horariosAtencion = normalizarHorariosAtencion(horariosGuardados);

  const suspension = horariosAtencion.suspension || {};

  if (suspension.activa) {
    const suspensionTieneHora = Boolean(suspension.hasta);
    const suspensionExpirada =
      suspensionTieneHora && new Date(suspension.hasta) <= fechaActual;

    if (!suspensionExpirada) {
      return {
        abierta: false,
        razon: "suspension",
        turnosAbiertos: [],
        mensaje:
          suspension.mensaje ||
          horariosAtencion.mensajeCerrado ||
          "Por ahora no estamos recibiendo pedidos.",
        reaperturaTexto: suspension.hasta
          ? `Volvemos a recibir pedidos aproximadamente el ${formatearFechaHoraCliente(
              suspension.hasta,
            )}.`
          : "La tienda volverá a activarse manualmente.",
      };
    }
  }

  const turnosAbiertos = obtenerTurnosAbiertosAhora(
    horariosAtencion,
    fechaActual,
  );

  if (turnosAbiertos.length > 0) {
    return {
      abierta: true,
      razon: "abierta",
      turnosAbiertos,
      mensaje: "",
      reaperturaTexto: "",
    };
  }

  const proximaApertura = obtenerProximaApertura(horariosAtencion, fechaActual);

  let reaperturaTexto = "";

  if (proximaApertura) {
    const esHoy = proximaApertura.diaId === obtenerDiaActualId(fechaActual);

    reaperturaTexto = `Volvemos a recibir pedidos ${
      esHoy ? "hoy" : proximaApertura.diaLabel
    } a las ${formatearHoraCliente(proximaApertura.apertura)}.`;
  }

  return {
    abierta: false,
    razon: "fuera_horario",
    turnosAbiertos: [],
    mensaje:
      horariosAtencion.mensajeCerrado ||
      "Por ahora la tienda está cerrada. Puedes revisar el menú y volver en nuestro horario de atención.",
    reaperturaTexto,
  };
};
